// /frontend/src/pages/admin/BulkImportProducts.jsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import { FaUpload, FaFileAlt, FaCheckCircle, FaTimesCircle, FaArrowLeft } from 'react-icons/fa';
import { productService } from '../../services/productService';

const BulkImportProducts = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    
    // Validate file type
    if (selectedFile && selectedFile.type !== 'text/csv') {
      setError('Please select a CSV file');
      setFile(null);
      return;
    }
    
    setFile(selectedFile);
    setError(null);
  };
  
  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!file) {
      setError('Please select a file to upload');
      return;
    }
    
    try {
      setIsUploading(true);
      setUploadProgress(0);
      
      const formData = new FormData();
      formData.append('productsFile', file);
      
      const importResults = await productService.bulkImportProducts(
        formData,
        (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      );
      
      setResults(importResults);
      setIsUploading(false);
    } catch (err) {
      setError(err.message || 'An error occurred during import');
      setIsUploading(false);
    }
  };
  
  const handleDownloadTemplate = () => {
    // Create CSV template content
    const templateContent = [
      'name,description,productCode,price,salePrice,category,stock,images,modelUrl,dimensions,weight,materials,colors,isOnSale,isNewArrival,isBestSeller,variant_name',
      'Product Name,Product description,ABC123,100,,1-bedroom,10,,,,,,,FALSE,TRUE,FALSE,Light',
      'Product Name,Product description,ABC123,100,,1-bedroom,8,,,,,,,FALSE,TRUE,FALSE,Dark'
    ].join('\n');
    
    // Create and trigger download
    const blob = new Blob([templateContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'product_import_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-semibold text-gray-800">Bulk Import Products</h1>
          <Link
            to="/admin/products"
            className="flex items-center text-gray-600 hover:text-gray-800"
          >
            <FaArrowLeft className="mr-2" /> Back to Products
          </Link>
        </div>
        
        {/* Instructions */}
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <h2 className="text-lg font-medium text-gray-800 mb-4">Instructions</h2>
          <div className="prose prose-sm max-w-none">
            <p>
              Upload a CSV file with your product data. For products with multiple variants
              (like different colors), use the same product code for all variants and specify
              each variant in the <code>variant_name</code> column.
            </p>
            
            <h3 className="font-medium mt-4">Required CSV Columns:</h3>
            <ul className="list-disc ml-5 space-y-1">
              <li><strong>name</strong> - Product name</li>
              <li><strong>description</strong> - Product description</li>
              <li><strong>productCode</strong> - Unique product code (use same code for variants)</li>
              <li><strong>price</strong> - Regular price</li>
              <li><strong>category</strong> - Product category (1-bedroom, 2-bedroom, 3-bedroom, etc.)</li>
              <li><strong>stock</strong> - Stock quantity</li>
            </ul>

            <h3 className="font-medium mt-4">Optional CSV Columns:</h3>
            <ul className="list-disc ml-5 space-y-1">
              <li><strong>salePrice</strong> - Sale price (if on sale)</li>
              <li><strong>images</strong> - Comma-separated list of image URLs</li>
              <li><strong>modelUrl</strong> - 3D model URL</li>
              <li><strong>dimensions</strong> - Format: "width,depth,height"</li>
              <li><strong>weight</strong> - Product weight</li>
              <li><strong>materials</strong> - Comma-separated list of materials</li>
              <li><strong>colors</strong> - Comma-separated list of colors</li>
              <li><strong>isOnSale</strong> - TRUE or FALSE</li>
              <li><strong>isNewArrival</strong> - TRUE or FALSE</li>
              <li><strong>isBestSeller</strong> - TRUE or FALSE</li>
              <li><strong>variant_name</strong> - Variant name (e.g., "Light", "Dark")</li>
            </ul>
              
            <button
              type="button"
              onClick={handleDownloadTemplate}
              className="mt-4 flex items-center text-teal-600 hover:text-teal-800"
            >
              <FaFileAlt className="mr-1" /> 
              Download CSV Template
            </button>
          </div>
        </div>
        
        {/* Upload Form */}
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <h2 className="text-lg font-medium text-gray-800 mb-4">Upload Products</h2>
          
          <form onSubmit={handleUpload}>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select CSV File
              </label>
              
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                <div className="space-y-1 text-center">
                  <FaUpload className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-sm text-gray-600">
                    <label
                      htmlFor="file-upload"
                      className="relative cursor-pointer rounded-md font-medium text-teal-600 hover:text-teal-500 focus-within:outline-none"
                    >
                      <span>Upload a file</span>
                      <input
                        id="file-upload"
                        name="file-upload"
                        type="file"
                        accept=".csv"
                        className="sr-only"
                        onChange={handleFileChange}
                        disabled={isUploading}
                      />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500">CSV files only</p>
                </div>
              </div>
              
              {file && (
                <div className="mt-2 flex items-center text-sm text-gray-700">
                  <FaFileAlt className="mr-2 text-teal-600" />
                  {file.name}
                </div>
              )}
              
              {error && (
                <p className="mt-2 text-sm text-red-600">{error}</p>
              )}
            </div>
            
            <div>
              <button
                type="submit"
                disabled={!file || isUploading}
                className={`px-4 py-2 rounded-md text-white ${
                  !file || isUploading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-teal-600 hover:bg-teal-700'
                }`}
              >
                {isUploading ? 'Uploading...' : 'Import Products'}
              </button>
            </div>
            
            {isUploading && (
              <div className="mt-4">
                <div className="relative pt-1">
                  <div className="text-xs text-right mb-1">
                    {uploadProgress}%
                  </div>
                  <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-teal-200">
                    <div
                      style={{ width: `${uploadProgress}%` }}
                      className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-teal-500 transition-all duration-300"
                    ></div>
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>
        
        {/* Results */}
        {results && (
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-lg font-medium text-gray-800 mb-4">Import Results</h2>
            
            <div className="mb-4 grid grid-cols-2 gap-4">
              <div className="bg-green-50 border border-green-100 rounded-md p-4">
                <div className="flex items-center">
                  <FaCheckCircle className="text-green-500 mr-2" />
                  <h3 className="text-green-700 font-medium">Successful Imports</h3>
                </div>
                <p className="text-2xl font-bold text-green-700 mt-2">{results.success}</p>
              </div>
              
              <div className="bg-red-50 border border-red-100 rounded-md p-4">
                <div className="flex items-center">
                  <FaTimesCircle className="text-red-500 mr-2" />
                  <h3 className="text-red-700 font-medium">Failed Imports</h3>
                </div>
                <p className="text-2xl font-bold text-red-700 mt-2">{results.failed}</p>
              </div>
            </div>
            
            {results.errors && results.errors.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Error Details</h3>
                <div className="bg-gray-50 p-4 rounded-md max-h-60 overflow-y-auto">
                  <ul className="text-xs text-red-600 list-disc list-inside">
                    {results.errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            
            <div className="mt-6 flex justify-between">
              <button
                type="button"
                onClick={() => {
                  setFile(null);
                  setResults(null);
                  setError(null);
                }}
                className="text-teal-600 hover:text-teal-800"
              >
                Import Another File
              </button>
              
              <button
                type="button"
                onClick={() => navigate('/admin/products')}
                className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-md"
              >
                Go to Products
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default BulkImportProducts;